import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const OUTREACH_STAGES = [
  {
    value: "not_contacted",
    label: "Not Contacted",
  },
  {
    value: "initial_contact",
    label: "Initial Contact",
  },
  {
    value: "followup_1",
    label: "Follow-up 1",
  },
  {
    value: "followup_2",
    label: "Follow-up 2",
  },
  {
    value: "followup_3",
    label: "Follow-up 3",
  },
  {
    value: "followup_4",
    label: "Follow-up 4",
  },
  {
    value: "followup_5",
    label: "Follow-up 5",
  },
  {
    value: "followup_6",
    label: "Follow-up 6",
  },
  {
    value: "final_followup",
    label: "Final Follow-up",
  },
  {
    value: "final_personal_attempt",
    label: "Final Personal Attempt",
  },
  {
    value: "closed_no_response",
    label: "Closed / No Response",
  },
  {
    value: "replied",
    label: "Replied",
  },
  {
    value: "activated",
    label: "Activated",
  },
];

const MESSAGE_STYLES = [
  {
    value: "made_for_you",
    label: "Made For You",
  },
  {
    value: "friendly",
    label: "Friendly",
  },
  {
    value: "restaurant_compliment",
    label: "Restaurant Compliment",
  },
  {
    // Keep the saved value for outreach-history compatibility; only the label/copy changed.
    value: "offer_focus",
    label: "Promotion / Event Focus",
  },
  {
    value: "results_focused",
    label: "Results Focused",
  },
  {
    value: "owner_to_owner",
    label: "Owner to Owner",
  },
  {
    value: "curiosity",
    label: "Curiosity",
  },
  {
    value: "low_pressure",
    label: "Low Pressure",
  },
  {
    value: "customer_action",
    label: "Customer Action",
  },
];

const OUTREACH_CHANNELS = [
  {
    value: "instagram_dm",
    label: "Instagram DM",
    description: "Short, conversational message for Instagram.",
  },
  {
    value: "email",
    label: "Email",
    description: "More detailed message for restaurant owners.",
  },
  {
    value: "facebook",
    label: "Facebook",
    description: "Friendly message for a Facebook business page.",
  },
  {
    value: "sms",
    label: "SMS",
    description: "Concise text message with a direct link.",
  },
  {
    value: "phone_script",
    label: "Phone Script",
    description: "A natural script for a live phone conversation.",
  },
  {
    value: "voicemail",
    label: "Voicemail",
    description: "A brief voicemail that encourages a response.",
  },
];

const STYLE_PHRASES = {
  made_for_you: {
    opening: ({ restaurantName }) =>
      `I created a TapToTable Page specifically for ${restaurantName}.`,
    benefit:
      "I built the page first so you can see exactly what your customers would experience before making any decision.",
  },

  friendly: {
    opening: ({ restaurantName }) =>
      `I came across ${restaurantName} and wanted to show you something I put together.`,
    benefit:
      "It is a simple, no-pressure way to see how TapToTable could work for your restaurant.",
  },

  restaurant_compliment: {
    opening: ({ restaurantName }) =>
      `${restaurantName} caught my attention, and I thought it would be a strong fit for TapToTable.`,
    benefit:
      "I created a page that highlights your restaurant and gives customers a clear next step after discovering you online.",
  },

  offer_focus: {
    opening: ({ restaurantName }) =>
      `I created a TapToTable Page for ${restaurantName} that can feature a promotion or event.`,
    benefit:
      "It gives customers a simple mobile page with one clear action, whether that is claiming a promotion, viewing an event, ordering, reserving, calling, or getting directions.",
  },

  results_focused: {
    opening: ({ restaurantName }) =>
      `I built a TapToTable Page for ${restaurantName} to help turn more online attention into customer visits.`,
    benefit:
      "The page gives people a direct action to take instead of simply viewing your social content and scrolling away.",
  },

  owner_to_owner: {
    opening: ({ restaurantName }) =>
      `I am the owner of TapToTable, and I personally created a page for ${restaurantName}.`,
    benefit:
      "As a fellow business owner, I believe it is better to see the finished product before being asked to pay for anything.",
  },

  curiosity: {
    opening: ({ restaurantName }) =>
      `I made something for ${restaurantName} that I thought you might want to see.`,
    benefit:
      "It is a custom customer-facing TapToTable Page designed around your restaurant, with room for promotions, events, and clear customer actions.",
  },

  low_pressure: {
    opening: ({ restaurantName }) =>
      `I created a TapToTable Page for ${restaurantName}, and there is no obligation to view it.`,
    benefit:
      "I wanted you to see the completed page first and decide for yourself whether it could be useful.",
  },

  customer_action: {
    opening: ({ restaurantName }) =>
      `I built a TapToTable Page that gives customers a clear next step after discovering ${restaurantName} online.`,
    benefit:
      "Instead of leaving customers to figure out what to do next, the page gives them one clear action such as claiming a promotion, viewing an event, ordering, reserving, calling, or getting directions.",
  },
};

const CHANNEL_VARIATIONS = {
  instagram_dm: [
    ({ opening, benefit, reviewUrl }) =>
      `Hi! ${opening} ${benefit} Here is the page: ${reviewUrl}`,

    ({ opening, reviewUrl }) =>
      `Hey! ${opening} I wanted you to see it before discussing anything else. You can view it here: ${reviewUrl}`,

    ({ restaurantName, benefit, reviewUrl }) =>
      `Hi ${restaurantName}! ${benefit} I already created the page for you here: ${reviewUrl}`,

    ({ opening, reviewUrl }) =>
      `Hi! ${opening} Take a look whenever you have a moment: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hey ${restaurantName} — I already built your TapToTable Page so you can see exactly how it looks. Here it is: ${reviewUrl} If you want it live, it’s $59/month.`,

    ({ opening, benefit, reviewUrl }) =>
      `${opening} ${benefit} I would be interested to hear what you think: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi! I put together a custom TapToTable Page for ${restaurantName}. There is nothing to sign up for just to view it: ${reviewUrl} If you want it live for customers, it’s $59/month.`,

    ({ opening, reviewUrl }) =>
      `Hey there! ${opening} Your page is already built and ready to review: ${reviewUrl}`,
  ],

  email: [
    ({ restaurantName, opening, benefit, reviewUrl }) =>
      `Subject: I created a TapToTable Page for ${restaurantName}

Hi,

${opening}

${benefit}

Rather than asking you to imagine how it works, I built the page first so you can review the actual customer experience.

View your page here:
${reviewUrl}

Thank you,
Keith
TapToTable`,

    ({ restaurantName, opening, reviewUrl }) =>
      `Subject: Your custom ${restaurantName} page is ready

Hi,

${opening}

The page is already built and ready for you to review. There is no obligation, and you do not need to create an account just to see it.

${reviewUrl}

I would be glad to hear your thoughts.

Keith
TapToTable`,

    ({ restaurantName, benefit, reviewUrl }) =>
      `Subject: A TapToTable Page for ${restaurantName}

Hi,

I created a custom TapToTable Page for ${restaurantName}.

${benefit}

You can review the completed page here:

${reviewUrl}

Best,
Keith
TapToTable`,

    ({ restaurantName, opening, benefit, reviewUrl }) =>
      `Subject: See the TapToTable Page built for ${restaurantName}

Hello,

${opening}

${benefit}

I wanted you to see the completed page before discussing a subscription or making any decisions.

View the page:
${reviewUrl}

If you want this page live for customers, TapToTable is $59/month and includes your hosted TapToTable Page, secure TapToTable Dashboard, Promotions & Events updates, customer activity, hosting, and ongoing support.

Regards,
Keith`,

    ({ restaurantName, reviewUrl }) =>
      `Subject: I built this for ${restaurantName}

Hi,

I recently came across ${restaurantName} and created a custom TapToTable Page for the restaurant.

The goal is to give online visitors a clear next step through a promotion, event, or direct customer action.

You can see the completed page here:

${reviewUrl}

Thank you,
Keith
TapToTable`,

    ({ restaurantName, benefit, reviewUrl }) =>
      `Subject: Turn more online attention into restaurant visits

Hi,

I built a TapToTable Page specifically for ${restaurantName}.

${benefit}

The page is ready now, and you can review it here:

${reviewUrl}

There is no commitment to view it.

Keith
TapToTable`,

    ({ restaurantName, opening, reviewUrl }) =>
      `Subject: A page created specifically for ${restaurantName}

Hello,

${opening}

I believe restaurant owners should be able to see the finished page before deciding whether it is useful.

Here is your completed page:

${reviewUrl}

I would appreciate your feedback.

Keith`,

    ({ restaurantName, reviewUrl }) =>
      `Subject: Your TapToTable Page

Hi,

I created a TapToTable Page for ${restaurantName} that customers can use to view a promotion or event and take a clear next action from their phone.

You can review the page here:

${reviewUrl}

The page has already been built, so you can evaluate the actual experience rather than a generic example.

Best,
Keith
TapToTable`,
  ],

  facebook: [
    ({ opening, benefit, reviewUrl }) =>
      `Hi! ${opening} ${benefit} You can view the page here: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hello ${restaurantName}! I created a custom TapToTable Page for your restaurant. It is already completed and ready to review: ${reviewUrl}`,

    ({ opening, reviewUrl }) =>
      `${opening} I wanted to send the finished page so you could see it for yourself: ${reviewUrl}`,

    ({ restaurantName, benefit, reviewUrl }) =>
      `Hi ${restaurantName}! ${benefit} I created your page here: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hello! I thought TapToTable could be a good fit for ${restaurantName}, so I built your page before contacting you. Take a look here: ${reviewUrl}`,

    ({ opening, benefit, reviewUrl }) =>
      `Hi there! ${opening} ${benefit} There is no obligation to view it: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi! Your custom TapToTable Page for ${restaurantName} is ready. I would love to know what you think: ${reviewUrl}`,

    ({ opening, reviewUrl }) =>
      `${opening} The page gives customers a simple promotion-or-event experience and a clear action to take. View it here: ${reviewUrl}`,
  ],

  sms: [
    ({ restaurantName, reviewUrl }) =>
      `Hi, I created a TapToTable Page for ${restaurantName}. You can view the completed page here: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi ${restaurantName}! I built your TapToTable Page first so you can see it before deciding anything: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `I made a custom TapToTable Page for ${restaurantName}. No obligation—here is the page: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Your TapToTable Page for ${restaurantName} is ready to review: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi! I created a page to help ${restaurantName} turn more online visitors into customers: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `I built this TapToTable Page specifically for ${restaurantName}: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, I wanted you to see the TapToTable Page I created for ${restaurantName}: ${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `I created a custom restaurant page for ${restaurantName}. Take a look when you have a moment: ${reviewUrl}`,
  ],

  phone_script: [
    ({ restaurantName, opening, benefit, reviewUrl }) =>
      `Hi, my name is Keith, and I am the owner of TapToTable.

${opening}

${benefit}

I am not calling to ask you to buy something without seeing it. The page is already completed.

I can text or email you the link now. If you decide you want the page live for customers, TapToTable is $59 per month.

The page address is:

${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith with TapToTable.

I created a custom TapToTable Page for ${restaurantName} before reaching out.

The idea is to give people who find you online a clear next step, such as viewing a promotion or event, ordering, reserving, calling, or getting directions.

The page is already built. May I text or email you the link?

${reviewUrl}`,

    ({ restaurantName, opening, reviewUrl }) =>
      `Hello, my name is Keith. I run TapToTable.

${opening}

I wanted you to see the actual page rather than listen to a long sales explanation.

Would it be alright if I texted or emailed the link to you?

${reviewUrl}`,

    ({ restaurantName, benefit, reviewUrl }) =>
      `Hi, this is Keith with TapToTable.

I built a page specifically for ${restaurantName}.

${benefit}

There is no obligation. I would just like to send you the completed page so you can see whether it makes sense for the restaurant.

${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, my name is Keith, and I created a TapToTable Page for ${restaurantName}.

It is a customer-facing page where visitors can view a promotion or event and take a clear next action.

I already completed the page for you.

What is the best email or mobile number for me to send the link to?

${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hello, this is Keith from TapToTable.

I found ${restaurantName} online and thought your restaurant would be a strong fit.

I built your page before calling so you could evaluate something real instead of a generic presentation.

May I send you the link?

${reviewUrl}`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith.

I created a custom TapToTable Page for ${restaurantName} that gives customers one clear promotion-or-event experience and an easy action to take.

The page is already ready.

Could I send it to the owner or manager for review?

${reviewUrl}`,

    ({ restaurantName, opening, reviewUrl }) =>
      `Hi, my name is Keith, and I own TapToTable.

${opening}

I am simply trying to get the completed page in front of the owner or manager.

Who would be the best person to send it to?

${reviewUrl}`,
  ],

  voicemail: [
    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith with TapToTable. I created a custom TapToTable Page for ${restaurantName}, and the page is already completed and ready for you to review. I will send the link as well, or you can contact me when you have a moment. The page is ${reviewUrl}. Thank you.`,

    ({ restaurantName, reviewUrl }) =>
      `Hello, this is Keith, the owner of TapToTable. I built a custom TapToTable Page specifically for ${restaurantName}. It can feature promotions, events, and clear customer actions. There is no obligation—I wanted you to see the finished page first. I will send the link, and I would appreciate your feedback. Thank you.`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith with TapToTable. I created a page designed to help ${restaurantName} turn more online attention into customer visits. It is already built. I will send you the link at ${reviewUrl}. Please take a look when you have a chance.`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, my name is Keith. I created a TapToTable Page for ${restaurantName} before reaching out. The page can feature a promotion or event and gives customers a clear action to take. I will send the completed page link as well. Thank you.`,

    ({ restaurantName, reviewUrl }) =>
      `Hello, this is Keith from TapToTable. I made something specifically for ${restaurantName} that I think the owner or manager may want to see. The page is ready at ${reviewUrl}. Please call or message me when convenient.`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith. I built a custom restaurant page for ${restaurantName}, and I wanted to make sure the owner had an opportunity to see it. I will send the page link. There is no commitment to review it. Thank you.`,

    ({ restaurantName, reviewUrl }) =>
      `Hello, this is Keith with TapToTable. Your custom page for ${restaurantName} is complete. It was designed to turn online attention into a clear customer action. I will send the link at ${reviewUrl}, and I would love to hear what you think.`,

    ({ restaurantName, reviewUrl }) =>
      `Hi, this is Keith, the owner of TapToTable. I personally created a page for ${restaurantName}. I believe it is better to see the finished product before discussing anything else. I will send the link, and you can review it whenever you have time.`,
  ],
};

const STAGE_DETAILS = {
  not_contacted: {
    label: "Not Contacted",
    statusClass:
      "border-zinc-500/30 bg-zinc-500/10 text-zinc-300",
    nextAction: "Generate and send the initial contact.",
  },

  initial_contact: {
    label: "Initial Contact",
    statusClass:
      "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
    nextAction: "Schedule or prepare Follow-up 1.",
  },

  followup_1: {
    label: "Follow-up 1",
    statusClass:
      "border-amber-400/30 bg-amber-400/10 text-amber-300",
    nextAction: "Send the first follow-up message.",
  },

    followup_2: {
    label: "Follow-up 2",
    statusClass:
      "border-orange-400/30 bg-orange-400/10 text-orange-300",
    nextAction: "Send the second follow-up message.",
  },

  followup_3: {
    label: "Follow-up 3",
    statusClass:
      "border-orange-400/30 bg-orange-400/10 text-orange-300",
    nextAction: "Send the third follow-up message.",
  },

  followup_4: {
    label: "Follow-up 4",
    statusClass:
      "border-orange-400/30 bg-orange-400/10 text-orange-300",
    nextAction: "Send the fourth follow-up message.",
  },

  followup_5: {
    label: "Follow-up 5",
    statusClass:
      "border-rose-400/30 bg-rose-400/10 text-rose-300",
    nextAction: "Send the fifth follow-up message. Target cadence: Day 18.",
  },

  followup_6: {
    label: "Follow-up 6",
    statusClass:
      "border-rose-400/30 bg-rose-400/10 text-rose-300",
    nextAction: "Send the sixth follow-up message. Target cadence: Day 25.",
  },

  final_followup: {
    label: "Final Follow-up",
    statusClass:
      "border-rose-400/30 bg-rose-400/10 text-rose-300",
    nextAction: "Send the seventh and final follow-up message. Target cadence: Day 35.",
  },

  final_personal_attempt: {
    label: "Final Personal Attempt",
    statusClass:
      "border-amber-400/30 bg-amber-400/10 text-amber-300",
    nextAction:
      "Make one last manual contact by email, phone, voicemail, or SMS. Target cadence: Day 45.",
  },

  closed_no_response: {
    label: "Closed / No Response",
    statusClass:
      "border-zinc-500/30 bg-zinc-500/10 text-zinc-400",
    nextAction:
      "Outreach is complete. Reopen the stage only if you decide to pursue this restaurant again.",
  },

  replied: {
    label: "Replied",
    statusClass:
      "border-violet-400/30 bg-violet-400/10 text-violet-300",
    nextAction: "Respond personally and determine the next step.",
  },

  activated: {
    label: "Activated",
    statusClass:
      "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    nextAction: "Restaurant is activated.",
  },
};

function normalizeReviewUrl(reviewUrl) {
  if (!reviewUrl) {
    return "[RESTAURANT PAGE LINK]";
  }

  return reviewUrl;
}

function getStyleLabel(styleValue) {
  return (
    MESSAGE_STYLES.find((style) => style.value === styleValue)
      ?.label || styleValue
  );
}

function getChannelLabel(channelValue) {
  return (
    OUTREACH_CHANNELS.find(
      (channel) => channel.value === channelValue
    )?.label || channelValue
  );
}

function getStageLabel(stageValue) {
  return (
    OUTREACH_STAGES.find((stage) => stage.value === stageValue)
      ?.label || stageValue
  );
}

function cleanCampaignDisplayTitle(value = "") {
  return String(value || "")
    .replace(/\bPromotion(?:\s+Promotion)+\b/gi, "Promotion")
    .replace(/\bEvent(?:\s+Event)+\b/gi, "Event")
    .replace(/\s+/g, " ")
    .trim();
}

function ownerFacingPromotionPhrase(value = "") {
  const cleaned = cleanCampaignDisplayTitle(value);
  const normalized = cleaned.toLowerCase();

  if (!cleaned) return "a timely promotion";

  if (
    normalized.includes("dollar-off") ||
    normalized.includes("dollar off")
  ) {
    return "a dollar-off offer";
  }

  if (normalized.endsWith(" promotion")) {
    return cleaned.replace(/\s+promotion$/i, "").trim() || "a timely promotion";
  }

  return cleaned;
}

function sentStageForCurrentStage(stage) {
  switch (stage) {
    case "not_contacted":
      return "initial_contact";
    case "initial_contact":
      return "initial_contact";
    case "followup_1":
      return "followup_1";
    case "followup_2":
      return "followup_2";
    case "followup_3":
      return "followup_3";
    case "followup_4":
      return "followup_4";
    case "followup_5":
      return "followup_5";
    case "followup_6":
      return "followup_6";
    case "final_followup":
      return "final_followup";
    case "final_personal_attempt":
      return "final_personal_attempt";
    case "closed_no_response":
      return "closed_no_response";
    case "replied":
      return "replied";
    case "activated":
      return "activated";
    default:
      return "initial_contact";
  }
}

function nextStageAfterSentStage(stage) {
  switch (stage) {
    case "initial_contact":
      return "followup_1";
    case "followup_1":
      return "followup_2";
    case "followup_2":
      return "followup_3";
    case "followup_3":
      return "followup_4";
    case "followup_4":
      return "followup_5";
    case "followup_5":
      return "followup_6";
    case "followup_6":
      return "final_followup";
    case "final_followup":
      return "final_personal_attempt";
    case "final_personal_attempt":
      return "closed_no_response";
    case "closed_no_response":
      return "closed_no_response";
    case "replied":
      return "replied";
    case "activated":
      return "activated";
    default:
      return "followup_1";
  }
}

function stageRequiresFollowupDate(stage) {
  return ![
    "closed_no_response",
    "replied",
    "activated",
  ].includes(stage);
}

function parseDateValue(value) {
  if (!value) return null;

  const [year, month, day] = value
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function formatDateValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value) {
  const date = parseDateValue(value);

  if (!date) {
    return "Choose follow-up date";
  }

  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const OUTREACH_STAGE_DAY_OFFSETS = {
  initial_contact: 0,
  followup_1: 2,
  followup_2: 5,
  followup_3: 8,
  followup_4: 11,
  followup_5: 18,
  followup_6: 25,
  final_followup: 35,
  final_personal_attempt: 45,
};

function dateFromHistoryTimestamp(value) {
  if (!value) return null;

  const raw = String(value).trim();

  // Preserve the saved calendar date exactly.
  // ISO timestamps are often stored in UTC, and converting them to local time
  // before extracting year/month/day can shift the outreach cadence by one day.
  const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );
  }

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );
}

function initialContactDateFromHistory(history = []) {
  const initialEntry = history.find(
    (item) =>
      String(item?.stage || item?.sent_stage || "")
        .trim()
        .toLowerCase() === "initial_contact"
  );

  return dateFromHistoryTimestamp(
    initialEntry?.created_at ||
      initialEntry?.sent_at ||
      initialEntry?.last_contacted_at
  );
}

function scheduledDateForStage({
  stage,
  history = [],
  fallbackDate = new Date(),
}) {
  const offset = OUTREACH_STAGE_DAY_OFFSETS[stage];

  if (typeof offset !== "number") {
    return "";
  }

  const baseDate =
    initialContactDateFromHistory(history) ||
    new Date(
      fallbackDate.getFullYear(),
      fallbackDate.getMonth(),
      fallbackDate.getDate()
    );

  // All later stages remain anchored to the saved Day 0 calendar date.

  const scheduled = new Date(baseDate);
  scheduled.setDate(scheduled.getDate() + offset);

  return formatDateValue(scheduled);
}

function isSameDate(first, second) {
  if (!first || !second) return false;

  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}


const FOLLOWUP_CAMPAIGN_STAGE_KEYWORDS = {
  followup_1: [],
  followup_2: [
    "customer_action",
    "customer action",
    "conversion",
    "results",
    "order",
    "reserve",
    "call",
    "directions",
  ],
  followup_3: [
    "restaurant_intelligence",
    "restaurant intelligence",
    "menu",
    "item",
    "food",
    "spotlight",
    "special",
    "event",
  ],
  followup_4: [
    "owner_control",
    "owner control",
    "self_manage",
    "self manage",
    "dashboard",
    "editing",
    "edit",
    "manage",
  ],
  followup_5: [
    "urgency",
    "occasion",
    "happy hour",
    "event",
    "daypart",
    "lunch",
    "dinner",
    "weekend",
  ],
  followup_6: [
    "analytics",
    "measurement",
    "tracking",
    "results",
    "customer_action",
    "customer action",
    "response",
  ],
  final_followup: [
    "analytics",
    "results",
    "owner_control",
    "owner control",
    "easy",
    "ongoing",
    "dashboard",
  ],
};

function campaignSearchText(campaign = {}) {
  return [
    campaign.angle_family,
    campaign.subtype,
    campaign.title,
    campaign.origin,
    campaign.campaign_type,
    campaign.page_type,
    campaign.recommended_offer,
    campaign.customer_reason,
    campaign.verified_item,
    campaign.verified_event,
    campaign.verified_promotion,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function selectCampaignForStage({
  stage,
  campaignBank = [],
  history = [],
}) {
  if (
    !stage ||
    [
      "not_contacted",
      "initial_contact",
      "final_personal_attempt",
      "closed_no_response",
      "replied",
      "activated",
    ].includes(stage)
  ) {
    return null;
  }

  const usedFamilies = new Set(
    [
      ...history.map((item) => item?.campaign_angle_family),
      ...campaignBank
        .filter((campaign) => campaign?.used_in_outreach)
        .map((campaign) => campaign?.angle_family),
    ]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase())
  );

  const candidates = campaignBank.filter((campaign) => {
    if (
      !campaign ||
      !campaign.id ||
      campaign.active === false ||
      campaign.used_in_outreach
    ) {
      return false;
    }

    const searchText = campaignSearchText(campaign);
    const verifiedPromotion = String(
      campaign.verified_promotion || ""
    )
      .trim()
      .toLowerCase();

    // Backward-compatibility guard for Campaign Bank records created before
    // the explicit Happy Hour evidence rule. A Happy Hour angle is eligible
    // only when the bank entry itself carries a verified Happy Hour promotion.
    if (
      searchText.includes("happy hour") &&
      !verifiedPromotion.includes("happy hour")
    ) {
      return false;
    }

    return true;
  });

  if (!candidates.length) return null;

  const preferredKeywords =
    FOLLOWUP_CAMPAIGN_STAGE_KEYWORDS[stage] || [];

  const ranked = candidates
    .map((campaign, index) => {
      const text = campaignSearchText(campaign);
      const family = String(campaign.angle_family || "")
        .trim()
        .toLowerCase();

      let score = Number(campaign.confidence || 0) * 10;

      if (family && !usedFamilies.has(family)) {
        score += 25;
      }

      preferredKeywords.forEach((keyword, keywordIndex) => {
        if (text.includes(keyword)) {
          score += Math.max(5, 22 - keywordIndex);
        }
      });

      if (stage === "followup_1") {
        const origin = String(campaign.origin || "")
          .trim()
          .toLowerCase();

        if (campaign.verified_promotion || campaign.verified_event) {
          score += 55;
        } else if (campaign.verified_item) {
          score += 48;
        } else if (origin === "restaurant_intelligence") {
          score += 32;
        } else if (origin === "taptotable_product") {
          score -= 20;
        }
      }

      if (
        stage === "followup_3" &&
        String(campaign.origin || "").toLowerCase() ===
          "restaurant_intelligence"
      ) {
        score += 24;
      }

      if (
        stage === "followup_3" &&
        (campaign.verified_item ||
          campaign.verified_event ||
          campaign.verified_promotion)
      ) {
        score += 16;
      }

      return {
        campaign,
        score,
        index,
      };
    })
    .sort((first, second) => {
      if (second.score !== first.score) {
        return second.score - first.score;
      }

      return first.index - second.index;
    });

  return ranked[0]?.campaign || null;
}

function campaignOwnerFacingReason(campaign = {}) {
  const angleFamily = String(campaign.angle_family || "")
    .trim()
    .toLowerCase();
  const subtype = String(campaign.subtype || "")
    .trim()
    .toLowerCase();
  const origin = String(campaign.origin || "")
    .trim()
    .toLowerCase();

  const verifiedItem = String(campaign.verified_item || "").trim();
  const verifiedEvent = String(campaign.verified_event || "").trim();
  const verifiedPromotion = String(
    campaign.verified_promotion || ""
  ).trim();
  const recommendedOffer = String(
    campaign.recommended_offer || ""
  ).trim();

  const searchText = [
    angleFamily,
    subtype,
    campaign.title,
    origin,
    campaign.campaign_type,
    campaign.page_type,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    searchText.includes("owner_control") ||
    searchText.includes("owner control") ||
    searchText.includes("self_manage") ||
    searchText.includes("editing") ||
    searchText.includes("dashboard")
  ) {
    return (
      "You can update the promotion or event, photo, wording, button, " +
      "and restaurant details yourself from the TapToTable Dashboard, " +
      "so you are not waiting on a developer every time you want to change something."
    );
  }

  if (
    searchText.includes("customer_action") ||
    searchText.includes("customer action") ||
    searchText.includes("conversion") ||
    searchText.includes("results")
  ) {
    return (
      "The page gives people who find you on Instagram a clear next step " +
      "instead of simply viewing the profile and scrolling away. They can " +
      "claim a promotion, view an event, order, reserve, call, or get directions."
    );
  }

  if (
    searchText.includes("analytics") ||
    searchText.includes("measurement") ||
    searchText.includes("tracking")
  ) {
    return (
      "TapToTable also gives you a simple way to see whether people are " +
      "actually viewing the page and taking action, instead of guessing " +
      "what happens after they see you online."
    );
  }

  if (verifiedEvent) {
    return (
      `I noticed ${verifiedEvent} is something you can put in front of more people. ` +
      "That could be a strong reason to use the TapToTable Page, giving customers one clear place to see the event and take the next step."
    );
  }

  if (verifiedPromotion) {
    const promotionPhrase =
      ownerFacingPromotionPhrase(verifiedPromotion);

    const promotionIntro =
      promotionPhrase.toLowerCase().startsWith("a ") ||
      promotionPhrase.toLowerCase().startsWith("an ")
        ? `I noticed you already promote ${promotionPhrase}.`
        : `I noticed ${promotionPhrase} is already part of what you promote.`;

    return (
      `${promotionIntro} ` +
      `That could give people a timely reason to visit through the TapToTable Page and make the promotion easier to act on.`
    );
  }

  if (verifiedItem) {
    const offerText = recommendedOffer
      ? ` You could even feature it with something simple like ${recommendedOffer}.`
      : "";

    return (
      `I noticed ${verifiedItem} stands out as something worth featuring. ` +
      `That gives people a specific reason to stop in instead of just scrolling past.${offerText}`
    );
  }

  if (
    searchText.includes("urgency") ||
    searchText.includes("occasion") ||
    searchText.includes("daypart") ||
    searchText.includes("happy hour") ||
    searchText.includes("lunch") ||
    searchText.includes("dinner") ||
    searchText.includes("weekend")
  ) {
    return (
      "You can rotate timely promotions, events, and reasons to visit so the " +
      "page stays useful without relying on one permanent discount."
    );
  }

  if (recommendedOffer) {
    return (
      `A simple way to use the page would be to feature something like ${recommendedOffer}. ` +
      "That gives people one clear reason to act instead of just viewing the restaurant online and moving on."
    );
  }

  return (
    "The idea is to give people who discover the restaurant online one clear " +
    "reason to take the next step instead of leaving them at the end of a social post."
  );
}

function finalPersonalAttemptText({
  restaurantName,
  reviewUrl,
  channel,
}) {
  const pageLine = reviewUrl
    ? `The TapToTable Page is still available here: ${reviewUrl}`
    : "The TapToTable Page is still available if you would like me to send it again.";

  if (channel === "email") {
    return `Subject: Last note about the TapToTable Page I built for ${restaurantName}

Hi,

I wanted to make one last personal attempt to reach the owner or manager about the TapToTable Page I built for ${restaurantName}.

${pageLine}

If it is not something you are interested in, no problem at all. I will close it out on my end.

Thanks for taking a look,

Keith
TapToTable`;
  }

  if (channel === "phone_script") {
    return `Hi, this is Keith with TapToTable.

I built a custom TapToTable Page for ${restaurantName}, and I have tried to get it in front of the owner or manager.

I am making one last personal attempt before I close it out on my end.

If someone would like to review it, I would be happy to text or email the page link.

${pageLine}`;
  }

  if (channel === "voicemail") {
    return (
      `Hi, this is Keith with TapToTable. I built a custom TapToTable Page for ${restaurantName}, ` +
      `and I am making one last personal attempt to reach the owner or manager before I close it out. ` +
      `If you would like to review the page, please call or message me back. ${pageLine} Thank you.`
    );
  }

  if (channel === "sms") {
    return (
      `Hi, this is Keith with TapToTable. I’m reaching out one last time about the TapToTable Page I built for ` +
      `${restaurantName}. ${pageLine} If it’s not a fit, no problem at all — I’ll close it out on my end.`
    );
  }

  return finalPersonalAttemptText({
    restaurantName,
    reviewUrl,
    channel: "email",
  });
}

function campaignStyleLead({
  style,
  restaurantName,
  stage,
  defaultLead,
}) {
  if (stage === "final_followup") {
    const finalLead = `I wanted to send one final note about the TapToTable Page I built for ${restaurantName}.`;

    const finalStyleTail = {
      made_for_you: "",
      friendly: " I just wanted to leave you with one last useful idea.",
      restaurant_compliment:
        ` ${restaurantName} stood out to me, so I wanted to make sure you had one last look at the page.`,
      offer_focus:
        " I wanted to leave you with one last promotion or event idea you could use on the page.",
      results_focused:
        " I wanted to leave you with one last practical way the page could help turn online attention into customer action.",
      owner_to_owner:
        " As the owner of TapToTable, I wanted to reach out personally one last time.",
      curiosity:
        " I wanted to leave you with one last idea you may find worth looking at.",
      low_pressure:
        " No pressure at all — I just wanted to leave the page with you one last time.",
      customer_action:
        " I wanted to leave you with one last way the page could give customers a clear next step.",
    }[style] || "";

    return `${finalLead}${finalStyleTail}`;
  }

  const styleLeads = {
    made_for_you: defaultLead,

    friendly:
      `Just wanted to check back with one more useful idea for ${restaurantName}.`,

    restaurant_compliment:
      `${restaurantName} stood out to me, so I wanted to follow up with one more idea that could fit the restaurant well.`,

    offer_focus:
      `I wanted to follow up with one more promotion or event angle ${restaurantName} could use on the TapToTable Page.`,

    results_focused:
      `I wanted to share one more practical way TapToTable could help turn online attention for ${restaurantName} into customer visits and actions.`,

    owner_to_owner:
      `As the owner of TapToTable, I wanted to follow up personally with one more idea for ${restaurantName}.`,

    curiosity:
      `I wanted to show you one more idea for ${restaurantName} that you may find worth a look.`,

    low_pressure:
      `No pressure at all — I just wanted to follow up with one more way ${restaurantName} could use the TapToTable Page.`,

    customer_action:
      `I wanted to follow up with one more way the TapToTable Page could give ${restaurantName} customers a clear next step.`,

  };

  return styleLeads[style] || defaultLead;
}

function campaignFollowupText({
  campaign,
  restaurantName,
  reviewUrl,
  stage,
  channel,
  style = "made_for_you",
}) {
  if (!campaign) return "";

  const reason = campaignOwnerFacingReason(campaign);

  const campaignText = campaignSearchText(campaign);

  const followup6Lead =
    campaign.verified_item
      ? `I wanted to follow up with one more idea for ${restaurantName}.`
      : campaignText.includes("analytics") ||
          campaignText.includes("measurement") ||
          campaignText.includes("tracking") ||
          campaignText.includes("results") ||
          campaignText.includes("response")
        ? `I wanted to share one more practical benefit of the TapToTable Page for ${restaurantName}.`
        : campaign.verified_event ||
            campaign.verified_promotion ||
            campaignText.includes("event")
          ? `I wanted to share one more timely way ${restaurantName} could use the TapToTable Page.`
          : `I wanted to follow up with one more idea for ${restaurantName}.`;

  const stageLead = {
    followup_1:
      `Just following up on the TapToTable Page I sent for ${restaurantName}.`,
    followup_2:
      `I wanted to share another practical way TapToTable could help ${restaurantName}.`,
    followup_3:
      `I wanted to follow up with one more idea for ${restaurantName}.`,
    followup_4:
      `One thing restaurant owners may appreciate about TapToTable is how easy the page is to manage.`,
    followup_5:
      `Another way ${restaurantName} could use the TapToTable Page is to keep giving customers timely reasons to visit.`,
    followup_6: followup6Lead,
    final_followup:
      `I wanted to send one final note about the TapToTable Page I built for ${restaurantName}.`,
  }[stage] || `A quick follow-up about the TapToTable Page for ${restaurantName}.`;

  const styledStageLead = campaignStyleLead({
    style,
    restaurantName,
    stage,
    defaultLead: stageLead,
  });

  if (channel === "email") {
    return `Subject: Another TapToTable idea for ${restaurantName}

Hi,

${styledStageLead}

${reason}

You can review the Restaurant Page here:
${reviewUrl}

There is no obligation. I just wanted to make sure you had the page and could see another practical way it could be used.

Keith
TapToTable`;
  }

  if (channel === "phone_script") {
    return `Hi, this is Keith with TapToTable.

${styledStageLead}

${reason}

I already built the Restaurant Page, so you can see the actual customer experience instead of a generic example.

The page is:
${reviewUrl}`;
  }

  if (channel === "voicemail") {
    return (
      `Hi, this is Keith with TapToTable. ${styledStageLead} ${reason} ` +
      `Your Restaurant Page is still available to review at ${reviewUrl}. Thank you.`
    );
  }

  if (channel === "sms") {
    if (stage === "final_followup") {
      return (
        `${styledStageLead} ${reason} I’ll leave the page here in case you’d like to revisit it: ` +
        `${reviewUrl} Thanks for taking a look — I won’t keep filling up your inbox.`
      );
    }

    return `${styledStageLead} ${reason} Here is the page: ${reviewUrl}`;
  }

  if (stage === "final_followup") {
    return (
      `${styledStageLead} ${reason} ` +
      `I’ll leave the page here in case you’d like to revisit it: ${reviewUrl} ` +
      `Thanks for taking a look — I won’t keep filling up your inbox.`
    );
  }

  return `${styledStageLead} ${reason} Take a look here: ${reviewUrl}`;
}


function FollowupCalendar({
  value,
  disabled = false,
  hasError = false,
  onChange,
}) {
  const selectedDate = parseDateValue(value);
  const initialDate = selectedDate || new Date();

  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );

  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();

  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const calendarCells = [];

  for (let index = 0; index < firstDayIndex; index += 1) {
    calendarCells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    calendarCells.push(new Date(year, month, day));
  }

  const changeMonth = (amount) => {
    setVisibleMonth(
      (previous) =>
        new Date(
          previous.getFullYear(),
          previous.getMonth() + amount,
          1
        )
    );
  };

  const chooseDate = (date) => {
    if (typeof onChange === "function") {
      onChange(formatDateValue(date));
    }

    setIsOpen(false);
  };

  const chooseToday = () => {
    const today = new Date();

    setVisibleMonth(
      new Date(today.getFullYear(), today.getMonth(), 1)
    );

    chooseDate(today);
  };

  const clearDate = () => {
    if (typeof onChange === "function") {
      onChange("");
    }

    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen((previous) => !previous);
          }
        }}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-xl border bg-[#121214] px-4 py-3 text-left text-white transition-all focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-60 ${
          hasError
            ? "border-rose-400 focus:border-rose-400 focus:ring-rose-400"
            : "border-white/10 hover:border-white/20 focus:border-[#00E5FF] focus:ring-[#00E5FF]"
        }`}
        data-testid="outreach-followup-date-button"
      >
        <span className={value ? "text-white" : "text-zinc-500"}>
          {formatDisplayDate(value)}
        </span>

        <span aria-hidden="true" className="text-zinc-400">
          ▦
        </span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 top-full z-30 mt-2 w-full min-w-[290px] rounded-xl border border-white/10 bg-[#18181C] p-4 shadow-2xl">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              aria-label="Previous month"
            >
              ←
            </button>

            <div className="text-sm font-semibold text-white">
              {visibleMonth.toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </div>

            <button
              type="button"
              onClick={() => changeMonth(1)}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              aria-label="Next month"
            >
              →
            </button>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
              (dayName) => (
                <div
                  key={dayName}
                  className="py-1 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500"
                >
                  {dayName}
                </div>
              )
            )}

            {calendarCells.map((date, index) => {
              if (!date) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="h-9"
                  />
                );
              }

              const isSelected = isSameDate(date, selectedDate);
              const isToday = isSameDate(date, new Date());

              return (
                <button
                  key={formatDateValue(date)}
                  type="button"
                  onClick={() => chooseDate(date)}
                  className={`h-9 rounded-lg text-sm font-medium transition-colors ${
                    isSelected
                      ? "bg-[#00E5FF] text-black"
                      : isToday
                        ? "border border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]"
                        : "text-zinc-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
            <button
              type="button"
              onClick={clearDate}
              className="text-xs font-semibold text-zinc-400 transition-colors hover:text-white"
            >
              Clear
            </button>

            <button
              type="button"
              onClick={chooseToday}
              className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function OutreachPanel({
  restaurantName = "",
  reviewUrl = "",
  currentStage = "not_contacted",
  nextFollowupAt = "",
  history = [],
  campaignBank = [],
  saving = false,
  onStageChange,
  onScheduleChange,
  onMarkSent,
}) {
  const [selectedChannel, setSelectedChannel] =
    useState("instagram_dm");
  const [selectedStyle, setSelectedStyle] =
    useState("made_for_you");
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [variationIndex, setVariationIndex] = useState(0);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [followupDateError, setFollowupDateError] =
    useState(false);
  const [manualScheduleOverride, setManualScheduleOverride] =
    useState(false);
  const followupSectionRef = useRef(null);

  const safeRestaurantName =
    restaurantName?.trim() || "your restaurant";

  const safeReviewUrl = normalizeReviewUrl(reviewUrl);

  const backToPagesUrl =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/staff/")
      ? "/staff/mockups"
      : "/admin/mockups";

  const currentStageDetails =
    STAGE_DETAILS[currentStage] || STAGE_DETAILS.not_contacted;

  const requiresFollowupDate =
    stageRequiresFollowupDate(currentStage);

  const selectedCampaign = useMemo(
    () =>
      selectCampaignForStage({
        stage: currentStage,
        campaignBank,
        history,
      }),
    [currentStage, campaignBank, history]
  );

  const displayedChannels = useMemo(() => {
    if (currentStage === "final_personal_attempt") {
      const allowed = new Set([
        "email",
        "phone_script",
        "voicemail",
        "sms",
      ]);

      return OUTREACH_CHANNELS.filter((channel) =>
        allowed.has(channel.value)
      );
    }

    return OUTREACH_CHANNELS;
  }, [currentStage]);

  const selectedChannelDetails = useMemo(() => {
    return (
      displayedChannels.find(
        (channel) => channel.value === selectedChannel
      ) || displayedChannels[0] || OUTREACH_CHANNELS[0]
    );
  }, [displayedChannels, selectedChannel]);

  const availableTemplates = useMemo(() => {
    return (
      CHANNEL_VARIATIONS[selectedChannel] ||
      CHANNEL_VARIATIONS.instagram_dm
    );
  }, [selectedChannel]);

  const buildMessage = (index) => {
    if (currentStage === "final_personal_attempt") {
      return finalPersonalAttemptText({
        restaurantName: safeRestaurantName,
        reviewUrl: safeReviewUrl,
        channel: selectedChannel,
      });
    }

    if (selectedCampaign) {
      return campaignFollowupText({
        campaign: selectedCampaign,
        restaurantName: safeRestaurantName,
        reviewUrl: safeReviewUrl,
        stage: currentStage,
        channel: selectedChannel,
        style: selectedStyle,
      });
    }

    const style =
      STYLE_PHRASES[selectedStyle] ||
      STYLE_PHRASES.made_for_you;

    const template = availableTemplates[index];

    if (!template) return "";

    return template({
      restaurantName: safeRestaurantName,
      reviewUrl: safeReviewUrl,
      opening: style.opening({
        restaurantName: safeRestaurantName,
      }),
      benefit: style.benefit,
    });
  };

  useEffect(() => {
    setGeneratedMessage("");
    setVariationIndex(0);
    setHasGenerated(false);
    setManualScheduleOverride(false);
    setFollowupDateError(false);

    if (
      currentStage === "final_personal_attempt" &&
      !["email", "phone_script", "voicemail", "sms"].includes(
        selectedChannel
      )
    ) {
      setSelectedChannel("email");
    }
  }, [currentStage, selectedCampaign?.id, selectedChannel]);

  const showVariation = (index) => {
    const normalizedIndex =
      ((index % availableTemplates.length) +
        availableTemplates.length) %
      availableTemplates.length;

    setVariationIndex(normalizedIndex);
    setGeneratedMessage(buildMessage(normalizedIndex));
    setHasGenerated(true);
  };

  const generateMessage = () => {
    showVariation(0);
  };

  const showPreviousVariation = () => {
    showVariation(variationIndex - 1);
  };

  const showNextVariation = () => {
    showVariation(variationIndex + 1);
  };

  const showRandomVariation = () => {
    if (availableTemplates.length <= 1) {
      showVariation(0);
      return;
    }

    let randomIndex = variationIndex;

    while (randomIndex === variationIndex) {
      randomIndex = Math.floor(
        Math.random() * availableTemplates.length
      );
    }

    showVariation(randomIndex);
  };

  const selectChannel = (channelValue) => {
    setSelectedChannel(channelValue);
    setVariationIndex(0);
    setGeneratedMessage("");
    setHasGenerated(false);
  };

  const selectStyle = (styleValue) => {
    setSelectedStyle(styleValue);
    setVariationIndex(0);
    setGeneratedMessage("");
    setHasGenerated(false);
  };

  const copyText = async (text, successMessage) => {
    if (!text) {
      toast.error("There is nothing to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      console.error("Clipboard copy failed:", error);
      toast.error("Could not copy to clipboard");
    }
  };

  const handleMarkSent = async () => {
    if (saving) {
      return;
    }

    if (!generatedMessage.trim()) {
      toast.error("Generate a message before marking it sent");
      return;
    }

    if (typeof onMarkSent !== "function") {
      toast.error("Outreach saving is not connected yet");
      return;
    }

    const sentStage =
      sentStageForCurrentStage(currentStage);

    const nextStage =
      nextStageAfterSentStage(sentStage);

    const automaticNextFollowupAt =
      nextStage === "final_followup"
        ? scheduledDateForStage({
            stage: "final_followup",
            history:
              sentStage === "initial_contact"
                ? [
                    ...history,
                    {
                      stage: "initial_contact",
                      created_at: new Date().toISOString(),
                    },
                  ]
                : history,
          })
        : stageRequiresFollowupDate(nextStage)
          ? scheduledDateForStage({
              stage: nextStage,
              history:
                sentStage === "initial_contact"
                  ? [
                      ...history,
                      {
                        stage: "initial_contact",
                        created_at: new Date().toISOString(),
                      },
                    ]
                  : history,
            })
          : "";

    const nextScheduledDate =
      manualScheduleOverride && nextFollowupAt
        ? nextFollowupAt
        : automaticNextFollowupAt;

    await onMarkSent({
      message: generatedMessage,
      messageStyle: selectedStyle,
      channel: selectedChannel,
      variationNumber: variationIndex + 1,
      sentStage,
      nextStage,
      campaignId: selectedCampaign?.id || "",
      nextFollowupAt:
        sentStage === "final_personal_attempt" ||
        sentStage === "closed_no_response" ||
        sentStage === "replied" ||
        sentStage === "activated"
          ? ""
          : nextScheduledDate,
    });
  };

  return (
    <section
      className="mt-8 overflow-visible rounded-2xl border border-white/10 bg-[#121214]"
      data-testid="outreach-panel"
    >
      <div className="border-b border-white/10 px-5 py-5 md:px-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
          Sales Workflow
        </div>

        <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-2xl font-semibold text-white">
              Outreach
            </h2>

            <p className="mt-1 text-sm text-zinc-400">
              Open the restaurant page, choose a contact method,
              generate a message, and schedule the next action.
            </p>
          </div>

          <div
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${currentStageDetails.statusClass}`}
          >
            {currentStageDetails.label}
          </div>
        </div>
      </div>

      <div className="space-y-6 p-5 md:p-6">
        <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-4 py-3 text-sm text-emerald-200">
          <span className="font-semibold">
            Outreach progress saves automatically.
          </span>{" "}
          Click Mark Sent to save the message, add it to history,
          advance the stage, and schedule the next follow-up.
        </div>

        {reviewUrl ? (
          <div className="rounded-xl border border-white/10 bg-[#1A1A1F] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              Restaurant Page
            </div>

            <p className="mt-2 text-sm text-zinc-400">
              Review the TapToTable Restaurant Page before contacting the
              restaurant.
            </p>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <a
                href={reviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="outreach-open-review"
              >
                View Restaurant Page ↗
              </a>

              <button
                type="button"
                onClick={() =>
                  copyText(
                    reviewUrl,
                    "Restaurant Page link copied"
                  )
                }
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="outreach-copy-review"
              >
                Copy Page Link
              </button>
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-[#1A1A1F] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              Current Stage
            </div>

            <div
              className={`mt-3 inline-flex rounded-full border px-3 py-1.5 text-sm font-semibold ${currentStageDetails.statusClass}`}
            >
              {currentStageDetails.label}
            </div>

            <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Next Suggested Step
            </div>

            <p className="mt-1 text-sm text-zinc-300">
              {currentStageDetails.nextAction}
            </p>

            <label
              htmlFor="outreach-stage"
              className="mt-4 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400"
            >
              Change Stage
            </label>

            <select
              id="outreach-stage"
              value={currentStage}
              onChange={(event) => {
                if (typeof onStageChange === "function") {
                  onStageChange(event.target.value);
                }
              }}
              disabled={saving}
              className="mt-2 w-full rounded-xl border border-white/10 bg-[#121214] px-4 py-3 text-white transition-all focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] disabled:opacity-60"
              data-testid="outreach-stage-select"
            >
              {OUTREACH_STAGES.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>

            <p className="mt-3 text-xs leading-5 text-zinc-500">
              Stages advance automatically when you click Mark Sent.
              Use this menu only to correct a stage or mark the
              restaurant as Replied or Activated.
            </p>
          </div>

          <div
            ref={followupSectionRef}
            className={`rounded-xl border bg-[#1A1A1F] p-4 ${
              followupDateError
                ? "border-rose-400/70"
                : "border-white/10"
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                {currentStage === "final_followup"
                  ? "Final Follow-up Date"
                  : currentStage === "final_personal_attempt"
                    ? "Final Personal Attempt Date"
                    : "Next Follow-up"}
              </div>

              {currentStage === "final_followup" && nextFollowupAt ? (
                <div className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300">
                  Final Follow-up Scheduled
                </div>
              ) : currentStage === "final_personal_attempt" && nextFollowupAt ? (
                <div className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-300">
                  Manual Attempt Scheduled
                </div>
              ) : requiresFollowupDate ? (
                <div className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-300">
                  {manualScheduleOverride
                    ? "Manual Override"
                    : "Auto Scheduled"}
                </div>
              ) : (
                <div className="rounded-full border border-zinc-500/30 bg-zinc-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                  No Next Date Required
                </div>
              )}
            </div>

            <p className="mt-2 text-sm text-zinc-400">
              {currentStage === "final_followup"
                ? "This is the Day 35 date for the seventh follow-up. When you mark it sent, TapToTable schedules one Final Personal Attempt for Day 45."
                : currentStage === "final_personal_attempt"
                  ? "This is the Day 45 manual final attempt. Use Email, Phone Script, Voicemail, or SMS. After Mark Sent, the lead closes as No Response."
                  : "TapToTable automatically schedules the next stage from the Day 0 / 2 / 5 / 8 / 11 / 18 / 25 / 35 / 45 cadence. Change the calendar only if you want to override it."}
            </p>

            <div className="mt-4">
              <FollowupCalendar
                value={nextFollowupAt}
                disabled={saving}
                hasError={followupDateError}
                onChange={(dateValue) => {
                  setFollowupDateError(false);
                  setManualScheduleOverride(true);

                  if (typeof onScheduleChange === "function") {
                    onScheduleChange(dateValue);
                  }
                }}
              />
            </div>

            {nextFollowupAt ? (
              <div className="mt-3 rounded-lg border border-[#00E5FF]/20 bg-[#00E5FF]/5 px-3 py-2 text-xs text-[#00E5FF]">
                {currentStage === "final_followup"
                  ? "Final follow-up scheduled for "
                  : currentStage === "final_personal_attempt"
                    ? "Final personal attempt scheduled for "
                    : "Scheduled for "}
                {formatDisplayDate(nextFollowupAt)}
              </div>
            ) : (
              <div
                className={`mt-3 text-xs ${
                  followupDateError
                    ? "font-semibold text-rose-300"
                    : "text-zinc-500"
                }`}
              >
                {requiresFollowupDate
                  ? currentStage === "followup_5"
                    ? "Mark Sent will automatically schedule Follow-up 6 for Day 25."
                    : currentStage === "followup_6"
                      ? "Mark Sent will automatically schedule the Final Follow-up for Day 35."
                      : "Mark Sent will automatically calculate the next follow-up date."
                  : currentStage === "final_followup"
                    ? "Mark Sent will automatically schedule the Final Personal Attempt for Day 45."
                    : currentStage === "final_personal_attempt"
                      ? "After this manual final attempt is sent, the lead closes as No Response."
                      : "No additional follow-up date is required for this stage."}
              </div>
            )}
          </div>
        </div>

        {selectedCampaign ? (
          <div
            className="rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4"
            data-testid="outreach-campaign-bank-selection"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
                  Campaign Bank · Selected for This Follow-up
                </div>
                <div className="mt-2 text-lg font-semibold text-white">
                  {cleanCampaignDisplayTitle(selectedCampaign.title) || "Campaign idea"}
                </div>
              </div>

              {typeof selectedCampaign.confidence === "number" ? (
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-zinc-300">
                  {Math.round(selectedCampaign.confidence * 100)}% confidence
                </div>
              ) : null}
            </div>

            {selectedCampaign.customer_reason ? (
              <p className="mt-3 text-sm leading-6 text-zinc-300">
                {selectedCampaign.customer_reason}
              </p>
            ) : null}

            {selectedCampaign.recommended_offer ? (
              <div className="mt-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-300">
                <span className="text-zinc-500">Suggested page angle:</span>{" "}
                {selectedCampaign.recommended_offer}
              </div>
            ) : null}

            <div className="mt-3 text-xs text-zinc-500">
              This unused campaign will be marked used only when you click Mark Sent.
            </div>
          </div>
        ) : null}

        {currentStage === "closed_no_response" ? (
          <div className="rounded-xl border border-zinc-500/30 bg-zinc-500/10 p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              Outreach Complete
            </div>

            <h3 className="mt-2 text-lg font-semibold text-white">
              No further contact is scheduled for this restaurant.
            </h3>

            <p className="mt-2 text-sm leading-6 text-zinc-400">
              The outreach sequence and Final Personal Attempt are complete.
              Reopen the stage manually only if you decide to pursue this
              restaurant again.
            </p>
          </div>
        ) : (
          <>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
            Contact Method
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {displayedChannels.map((channel) => {
              const isSelected =
                selectedChannel === channel.value;

              return (
                <button
                  key={channel.value}
                  type="button"
                  onClick={() => selectChannel(channel.value)}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    isSelected
                      ? "border-[#00E5FF] bg-[#00E5FF]/10"
                      : "border-white/10 bg-[#1A1A1F] hover:border-white/20 hover:bg-white/5"
                  }`}
                  data-testid={`outreach-channel-${channel.value}`}
                >
                  <div
                    className={`text-sm font-semibold ${
                      isSelected
                        ? "text-[#00E5FF]"
                        : "text-white"
                    }`}
                  >
                    {channel.label}
                  </div>

                  <div className="mt-1 text-xs leading-5 text-zinc-500">
                    {channel.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {currentStage !== "final_personal_attempt" ? (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
            Message Style
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {MESSAGE_STYLES.map((style) => {
              const isSelected =
                selectedStyle === style.value;

              return (
                <button
                  key={style.value}
                  type="button"
                  onClick={() => selectStyle(style.value)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all ${
                    isSelected
                      ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                      : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"
                  }`}
                  data-testid={`outreach-style-${style.value}`}
                >
                  {style.label}
                </button>
              );
            })}
          </div>
        </div>
        ) : (
          <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-sm text-amber-200">
            This is a manual terminal outreach. Choose Email, Phone Script, Voicemail, or SMS, generate the copy, contact the restaurant yourself, then click Mark Sent.
          </div>
        )}

        {!hasGenerated ? (
          <div className="rounded-xl border border-white/10 bg-[#1A1A1F] p-5">
            <div className="text-sm font-semibold text-white">
              {selectedChannelDetails.label}
            </div>

            <p className="mt-1 text-sm text-zinc-400">
              {currentStage === "final_personal_attempt"
                ? "Generate the ready-to-copy final personal outreach for this contact method."
                : (
                  <>
                    Generate one of eight available variations using the{" "}
                    {getStyleLabel(selectedStyle)} style.
                  </>
                )}
            </p>

            <button
              type="button"
              onClick={generateMessage}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-[#00E5FF] px-6 py-3 text-sm font-semibold text-black transition-all hover:bg-[#66FFFF] hover:shadow-[0_0_20px_rgba(0,229,255,0.35)]"
              data-testid="outreach-generate-message"
            >
              Generate {selectedChannelDetails.label}
            </button>
          </div>
        ) : null}

        {hasGenerated ? (
          <div className="rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
                  Generated Message
                </div>

                <div className="mt-1 text-xs text-zinc-400">
                  {getChannelLabel(selectedChannel)}
                  {currentStage === "final_personal_attempt" ? (
                    <> · Final Personal Attempt</>
                  ) : selectedCampaign ? (
                    <>
                      {" · "}
                      Campaign Bank: {cleanCampaignDisplayTitle(selectedCampaign.title) || "Selected campaign"}
                      {" · "}
                      {getStyleLabel(selectedStyle)}
                    </>
                  ) : (
                    <>
                      {" · "}
                      {getStyleLabel(selectedStyle)} · Variation{" "}
                      {variationIndex + 1} of{" "}
                      {availableTemplates.length}
                    </>
                  )}
                </div>
              </div>

              {!selectedCampaign && currentStage !== "final_personal_attempt" ? (
                <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={showPreviousVariation}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                >
                  ← Previous
                </button>

                <button
                  type="button"
                  onClick={showNextVariation}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Next →
                </button>

                <button
                  type="button"
                  onClick={showRandomVariation}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Random
                </button>
                </div>
              ) : null}
            </div>

            <textarea
              value={generatedMessage}
              onChange={(event) =>
                setGeneratedMessage(event.target.value)
              }
              rows={
                selectedChannel === "email" ||
                selectedChannel === "phone_script"
                  ? 14
                  : 8
              }
              className="mt-4 w-full resize-y rounded-xl border border-white/10 bg-[#121214] px-4 py-3 text-sm leading-6 text-white transition-all focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
              data-testid="outreach-generated-message"
            />

            <div className="mt-4 rounded-lg border border-emerald-400/20 bg-emerald-400/5 px-3 py-2 text-xs leading-5 text-emerald-200">
              {currentStage === "final_personal_attempt"
                ? "Mark Sent saves this manual attempt to history and closes the lead as No Response."
                : "Mark Sent automatically saves this outreach, adds it to history, advances the stage, and schedules the next follow-up."}
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={() =>
                  copyText(
                    generatedMessage,
                    `${selectedChannelDetails.label} copied`
                  )
                }
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="outreach-copy-message"
              >
                Copy Message
              </button>

              <button
                type="button"
                onClick={handleMarkSent}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-full bg-emerald-400 px-5 py-3 text-sm font-semibold text-black transition-all hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="outreach-mark-sent"
              >
                {saving ? "Saving Outreach…" : "Mark Sent"}
              </button>

              <a
                href={backToPagesUrl}
                className="inline-flex items-center justify-center rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-5 py-3 text-sm font-semibold text-[#00E5FF] transition-colors hover:bg-[#00E5FF]/20"
                data-testid="outreach-back-to-pages"
              >
                ← Back to Restaurant Pages
              </a>
            </div>
          </div>
        ) : null}

          </>
        )}

        <div className="rounded-xl border border-white/10 bg-[#1A1A1F] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
            Outreach History
          </div>

          {history.length ? (
            <div className="mt-4 space-y-4">
              {history.map((item, index) => (
                <div
                  key={item.id || `${item.stage}-${index}`}
                  className="rounded-xl border border-white/10 bg-[#121214] p-4"
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-400/15 text-xs font-bold text-emerald-300">
                      ✓
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white">
                        {item.label ||
                          `${getStageLabel(
                            item.stage
                          )} Sent`}
                      </div>

                      {item.created_at ? (
                        <div className="mt-1 text-xs text-zinc-500">
                          {new Date(
                            item.created_at
                          ).toLocaleString()}
                        </div>
                      ) : null}

                      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
                        {item.channel ? (
                          <div>
                            <span className="text-zinc-500">
                              Method:
                            </span>{" "}
                            <span className="text-zinc-300">
                              {getChannelLabel(item.channel)}
                            </span>
                          </div>
                        ) : null}

                        {item.message_style ? (
                          <div>
                            <span className="text-zinc-500">
                              Style:
                            </span>{" "}
                            <span className="text-zinc-300">
                              {getStyleLabel(
                                item.message_style
                              )}
                            </span>
                          </div>
                        ) : null}

                        {item.variation_number ? (
                          <div>
                            <span className="text-zinc-500">
                              Variation:
                            </span>{" "}
                            <span className="text-zinc-300">
                              {item.variation_number}
                            </span>
                          </div>
                        ) : null}
                      </div>

                      {(item.sent_by_name ||
                        item.sent_by_email ||
                        item.next_followup_at) ? (
                        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                          {item.sent_by_name ||
                          item.sent_by_email ? (
                            <div>
                              <span className="text-zinc-500">
                                Sent by:
                              </span>{" "}
                              <span className="text-zinc-300">
                                {item.sent_by_name ||
                                  item.sent_by_email}
                              </span>
                            </div>
                          ) : null}

                          {item.next_followup_at ? (
                            <div>
                              <span className="text-zinc-500">
                                Next follow-up:
                              </span>{" "}
                              <span className="text-zinc-300">
                                {formatDisplayDate(
                                  item.next_followup_at
                                )}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {item.message ? (
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <details>
                            <summary className="cursor-pointer text-xs font-semibold text-[#00E5FF]">
                              View sent message
                            </summary>

                            <div className="mt-2 whitespace-pre-wrap rounded-lg border border-white/10 bg-black/20 p-3 text-xs leading-5 text-zinc-400">
                              {item.message}
                            </div>
                          </details>

                          <button
                            type="button"
                            onClick={() =>
                              copyText(
                                item.message,
                                "Sent message copied"
                              )
                            }
                            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                            data-testid={`outreach-history-copy-${index}`}
                          >
                            Copy Message
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-zinc-500">
              No outreach has been logged yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
